import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/context/AuthContext';
import {
  CheckCircleIcon,
  UserPlusIcon,
  DocumentTextIcon,
  CubeIcon,
  CreditCardIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '@/services/api/client';
import { orderService } from '@/services/order.service';
import QRCode from 'qrcode';
import { authService } from '@/services/auth.service';
import { formatPrice, formatDateTime } from '@/utils/formatters';
import { Order, OrderItem } from '@/types/order.types';
import '@/styles/order-confirmation.css';
import { useTranslation } from 'react-i18next';

// Styles critiques injectés directement pour forcer l'application
const criticalStyles = `
  /* 1️⃣ QR Code scannable (priorité MAX) */
  #page-confirmation-commande .qr-container {
    width: 220px !important;
    height: 220px !important;
    min-width: 220px !important;
    min-height: 220px !important;
    margin: 20px auto !important;
    background: #ffffff !important;
    padding: 10px !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
    border: 1px solid #e0e0e0 !important;
  }

  #page-confirmation-commande img.qr-code {
    width: 200px !important;
    height: 200px !important;
    min-width: 200px !important;
    min-height: 200px !important;
    max-width: 200px !important;
    max-height: 200px !important;
    image-rendering: pixelated !important;
    object-fit: contain !important;
    display: block !important;
    background: white !important;
  }

  /* 2️⃣ Texte lisible (noir sur fond clair) */
  #page-confirmation-commande h1,
  #page-confirmation-commande h2,
  #page-confirmation-commande p,
  #page-confirmation-commande .montant,
  #page-confirmation-commande .text-blanc,
  #page-confirmation-commande strong {
    color: #000000 !important;
    text-shadow: none !important;
  }

  #page-confirmation-commande .confirmation-subtitle,
  #page-confirmation-commande .confirmation-muted,
  #page-confirmation-commande .confirmation-qr-label,
  #page-confirmation-commande .confirmation-meta,
  #page-confirmation-commande .confirmation-meta span,
  #page-confirmation-commande .summary-row,
  #page-confirmation-commande .summary-row span,
  #page-confirmation-commande .summary-row strong {
    color: #2c2c2c !important;
    text-shadow: none !important;
  }

  #page-confirmation-commande .summary-row.total .summary-value,
  #page-confirmation-commande .summary-row.total .montant,
  #page-confirmation-commande .summary-row.total strong {
    color: #000000 !important;
  }

  /* 3️⃣ Margin / padding top (sous navbar fixe) */
  #page-confirmation-commande main,
  #page-confirmation-commande > .flex-grow {
    margin-top: 80px !important;
    padding-top: 30px !important;
  }

  #page-confirmation-commande .order-confirmation-container {
    padding-top: 0 !important;
  }

  @media (max-width: 768px) {
    #page-confirmation-commande main,
    #page-confirmation-commande > .flex-grow {
      margin-top: 70px !important;
      padding-top: 24px !important;
    }
  }
`;

export const OrderConfirmation = () => {
  const { t, i18n } = useTranslation();
  const handlePrint = () => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    window.setTimeout(() => window.print(), 50);
  };
  const inlineSpacingStyle: React.CSSProperties = { marginTop: 80, paddingTop: 30 };
  const inlineQrContainerStyle: React.CSSProperties = {
    width: 220,
    height: 220,
    margin: '20px auto',
    background: '#ffffff',
    padding: 10,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    border: '1px solid #e0e0e0',
  };
  const inlineQrImageStyle: React.CSSProperties = {
    width: 200,
    height: 200,
    imageRendering: 'pixelated',
    objectFit: 'contain',
    display: 'block',
    background: '#ffffff',
  };
  const inlineBlockTextStyle: React.CSSProperties = { color: '#000000' };
  const inlineMutedTextStyle: React.CSSProperties = { color: '#2c2c2c' };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [registerErrors, setRegisterErrors] = useState<Record<string, string>>({});
  const [, setAccessUrl] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [, setAccessExpiresAt] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);

  const orderNumber = searchParams.get('orderNumber');
  const emailFromQuery = searchParams.get('email');
  const [orderMissingMessage, setOrderMissingMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!orderNumber) {
      navigate('/');
      return;
    }

    // Charger les détails de la commande (email requis côté API)
    const loadOrder = async () => {
      try {
        let email = emailFromQuery || '';
        if (!email) {
          const lastOrderRaw = sessionStorage.getItem('lastOrder');
          if (lastOrderRaw) {
            try {
              const lastOrder = JSON.parse(lastOrderRaw) as Order;
              if (lastOrder?.order_number === orderNumber && lastOrder.customer_email) {
                email = lastOrder.customer_email;
              }
            } catch {
              /* ignore */
            }
          }
        }
        if (!email && user?.email) {
          email = user.email;
        }

        if (!email) {
          // Fallback local uniquement si pas d'email pour l'API
          const lastOrderRaw = sessionStorage.getItem('lastOrder');
          if (lastOrderRaw) {
            const lastOrder = JSON.parse(lastOrderRaw) as Order;
            if (lastOrder?.order_number === orderNumber) {
              setOrder(lastOrder);
              setOrderId(lastOrder.id ?? null);
              setOrderMissingMessage(null);
              return;
            }
          }
          setOrder(null);
          setOrderMissingMessage('Email requis pour afficher les détails de la commande.');
          return;
        }

        const trackedOrder = await apiClient.get<Order>(
          `/orders/track-details/${orderNumber}?email=${encodeURIComponent(email)}`
        );
        setOrder(trackedOrder as any);
        setOrderId((trackedOrder as any)?.id ?? null);
        setOrderMissingMessage(null);
      } catch (error) {
        console.error('Erreur lors du chargement de la commande:', error);
        const lastOrderRaw = sessionStorage.getItem('lastOrder');
        if (lastOrderRaw) {
          try {
            const lastOrder = JSON.parse(lastOrderRaw) as Order;
            if (lastOrder?.order_number === orderNumber) {
              setOrder(lastOrder);
              setOrderId(lastOrder.id ?? null);
              setOrderMissingMessage(null);
              return;
            }
          } catch (parseError) {
            console.error('Erreur lors du parsing de lastOrder:', parseError);
          }
        }

        setOrder(null);
        setOrderMissingMessage('Aucune commande trouvée pour ce numéro. Vérifiez le lien ou contactez le support.');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderNumber, emailFromQuery, user?.email, navigate]);

  useEffect(() => {
    const loadAccessToken = async () => {
      if (!order || !orderNumber || !order.customer_email) return;
      try {
        const tokenResponse = await orderService.getAccessTokenByNumber(orderNumber, order.customer_email);
        setAccessUrl(tokenResponse.access_url);
        setAccessExpiresAt(tokenResponse.expires_at);
        // Utilisation de 256 pour une meilleure résolution
        const qr = await QRCode.toDataURL(tokenResponse.access_url, { width: 256, margin: 1 });
        setQrDataUrl(qr);
      } catch (error) {
        console.error('Erreur lors du chargement du token de commande:', error);
      }
    };

    loadAccessToken();
  }, [order, orderNumber]);

  useEffect(() => {
    const loadInvoice = async () => {
      if (!user || !orderId) return;
      try {
        const invoice = await apiClient.get<{ id: number }>(`/invoices/by-order/${orderId}`);
        setInvoicePdfUrl(`/api/invoices/${invoice.id}/pdf`);
      } catch (error) {
        setInvoicePdfUrl(null);
      }
    };

    loadInvoice();
  }, [user, orderId]);

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({ ...prev, [name]: value }));
    // Effacer l'erreur pour ce champ
    if (registerErrors[name]) {
      setRegisterErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateRegisterForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!registerForm.fullName.trim()) {
      newErrors.fullName = 'Le nom complet est requis';
    }

    if (!registerForm.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)) {
      newErrors.email = 'Format d\'email invalide';
    } else if (order && registerForm.email.toLowerCase() !== order.customer_email.toLowerCase()) {
      newErrors.email = 'L\'email doit correspondre à celui de la commande';
    }

    if (!registerForm.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (registerForm.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (!registerForm.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (registerForm.password !== registerForm.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setRegisterErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRegisterForm()) {
      return;
    }

    setRegistering(true);
    try {
      // Créer le compte
      await authService.register({
        fullName: registerForm.fullName,
        email: registerForm.email,
        password: registerForm.password,
      });

      // Connecter l'utilisateur automatiquement après l'inscription
      await login({
        email: registerForm.email,
        password: registerForm.password,
      });

      // Lier la commande à l'utilisateur
      if (orderNumber) {
        try {
          await apiClient.post(`/orders/link/${orderNumber}`);
        } catch (error) {
          console.error('Erreur lors de la liaison de la commande:', error);
          // Ne pas bloquer si la liaison échoue
        }
      }

      // Rediriger vers l'espace client
      navigate('/mes-commandes');
    } catch (error: any) {
      setRegisterErrors({
        general: error.response?.data?.error || error.message || 'Erreur lors de la création du compte',
      });
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div id="page-confirmation-commande" className="min-h-screen flex flex-col">
        <style>{criticalStyles}</style>
        <Navbar />
        <div className="flex-grow flex items-center justify-center" style={inlineSpacingStyle}>
          <div className="confirmation-loading">
            <div className="confirmation-spinner"></div>
            <p className="confirmation-loading-text" style={inlineMutedTextStyle}>{t('orderConfirmationPage.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div id="page-confirmation-commande" className="min-h-screen flex flex-col">
        <style>{criticalStyles}</style>
        <Navbar />
        <div className="flex-grow flex items-center justify-center" style={inlineSpacingStyle}>
          <div className="confirmation-error">
            <p className="confirmation-error-text" style={inlineMutedTextStyle}>
              {orderMissingMessage || t('orderConfirmationPage.notFound')}
            </p>
            <Link to="/" className="confirmation-error-link">
              {t('orderConfirmationPage.backHome')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const parsedItems: OrderItem[] = Array.isArray(order.items)
    ? (order.items as OrderItem[])
    : (() => {
        try {
          return order.items ? (JSON.parse(order.items as unknown as string) as OrderItem[]) : [];
        } catch (error) {
          console.error('Erreur lors du parsing des items de commande:', error);
          return [];
        }
      })();

  const computedSubtotal = order.subtotal && order.subtotal > 0
    ? order.subtotal
    : parsedItems.reduce((sum, item) => sum + (item.total || item.price * item.quantity), 0);

  const shippingCost = typeof order.shipping_cost === 'number' ? order.shipping_cost : 0;
  const taxes = 0;
  const discountAmount = 0;
  const computedTotal = order.total && order.total > 0
    ? order.total
    : computedSubtotal + shippingCost + taxes - discountAmount;

  const estimatedDelivery = order.created_at
    ? new Date(new Date(order.created_at).getTime() + 5 * 24 * 60 * 60 * 1000)
    : null;

  const statusSteps = [
    { key: 'pending', label: t('orderConfirmationPage.statusConfirmed') },
    { key: 'processing', label: t('orderConfirmationPage.statusPreparing') },
    { key: 'shipped', label: t('orderConfirmationPage.statusShipped') },
    { key: 'delivered', label: t('orderConfirmationPage.statusDelivered') },
  ];
  const currentStepIndex = statusSteps.findIndex(step => step.key === order.order_status);
  const statusLabel =
    order.order_status === 'pending' ? t('orderConfirmationPage.statusPending') :
    order.order_status === 'processing' ? t('orderConfirmationPage.statusConfirmed') :
    order.order_status === 'shipped' ? t('orderConfirmationPage.statusShipped') :
    order.order_status === 'delivered' ? t('orderConfirmationPage.statusDelivered') : t('orderConfirmationPage.statusCancelled');

  const paymentStatusLabel =
    order.payment_status === 'paid' ? t('orderConfirmationPage.paymentPaid') :
    order.payment_status === 'pending' ? t('orderConfirmationPage.paymentPending') : t('orderConfirmationPage.paymentFailed');

  const formatPriceDzd = (value: number) =>
    new Intl.NumberFormat(i18n.language.startsWith('en') ? 'en-US' : 'fr-FR', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const deliveryNotes = (order as any)?.notes || t('orderConfirmationPage.none');

  return (
    <div id="page-confirmation-commande" className="min-h-screen flex flex-col">
      <style>{criticalStyles}</style>
      <Navbar />

      <main className="flex-grow confirmation-page" style={inlineSpacingStyle}>
        <div className="order-confirmation-container">
          <nav className="confirmation-breadcrumb" aria-label={t('orderConfirmationPage.breadcrumb')}>
            <Link to="/" className="breadcrumb-link">{t('orderConfirmationPage.breadcrumbHome')}</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{t('orderConfirmationPage.breadcrumbCurrent')}</span>
          </nav>

          <div className="confirmation-block printable-order" style={inlineBlockTextStyle}>
            <div className="confirmation-hero">
              <div className="confirmation-title-block">
                <div className="confirmation-icon">
                  <CheckCircleIcon style={{ width: 28, height: 28 }} />
                </div>
                <div>
                  <h1>{t('orderConfirmationPage.confirmedTitle')}</h1>
                  <p className="confirmation-subtitle" style={inlineMutedTextStyle}>
                    {t('orderConfirmationPage.confirmedSubtitle')}
                  </p>
                  <div className="confirmation-meta">
                    <span><strong>{t('orderConfirmationPage.orderLabel')}</strong> {order.order_number}</span>
                    <span><strong>{t('orderConfirmationPage.dateLabel')}</strong> {formatDateTime(order.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="confirmation-qr">
                {qrDataUrl ? (
                  <>
                    <div className="qr-container" style={inlineQrContainerStyle}>
                      <img src={qrDataUrl} alt={t('orderConfirmationPage.qrAlt')} className="qr-code" style={inlineQrImageStyle} />
                    </div>
                    <span className="confirmation-qr-label" style={inlineMutedTextStyle}>
                      {t('orderConfirmationPage.scanQr')}
                    </span>
                    <div className="qr-actions">
                      <a href={qrDataUrl} download={`qr-${order.order_number}.png`} className="btn btn-secondary">
                        {t('orderConfirmationPage.downloadQr')}
                      </a>
                      <button type="button" className="btn btn-secondary" onClick={handlePrint}>
                        {t('orderConfirmationPage.print')}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="confirmation-muted" style={inlineMutedTextStyle}>{t('orderConfirmationPage.generatingQr')}</p>
                )}
              </div>
            </div>

            <div className="confirmation-sections">
              <section className="confirmation-section">
                <h2><DocumentTextIcon className="section-icon" style={{ width: 24, height: 24, display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('orderConfirmationPage.summary')}</h2>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.subtotal')}</span>
                  <strong>{formatPrice(computedSubtotal)}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.shipping')}</span>
                  <strong>{formatPrice(shippingCost)}</strong>
                </div>
                {order.tax_amount && order.tax_amount > 0 && (
                  <div className="summary-row">
                    <span>{t('orderConfirmationPage.taxes')}</span>
                    <strong>{formatPrice(order.tax_amount)}</strong>
                  </div>
                )}
                <div className="summary-row total">
                  <span className="summary-label">{t('orderConfirmationPage.total')}</span>
                  <strong className="summary-value montant">{formatPrice(computedTotal)}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.paymentMethod')}</span>
                  <strong>{t('checkoutPage.card')}</strong>
                </div>
              </section>

              <section className="confirmation-section">
                <h2><CubeIcon className="section-icon" style={{ width: 24, height: 24, display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('orderConfirmationPage.statusTitle')}</h2>
                <div className="status-current">{statusLabel}</div>
                <div className="status-steps">
                  {statusSteps.map((step, index) => {
                    const isCompleted = currentStepIndex >= index && currentStepIndex !== -1;
                    const isActive = index === currentStepIndex;
                    return (
                      <div key={step.key} className={`status-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}>
                        <div className="dot"></div>
                        <span>{step.label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="confirmation-muted">
                  {t('orderConfirmationPage.estimatedDelivery')} {estimatedDelivery ? formatDateTime(estimatedDelivery) : t('orderConfirmationPage.toConfirm')}
                </div>
              </section>

              <section className="confirmation-section">
                <h2><CreditCardIcon className="section-icon" style={{ width: 24, height: 24, display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('orderConfirmationPage.paymentTitle')}</h2>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.status')}</span>
                  <strong>{paymentStatusLabel}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.method')}</span>
                  <strong>{t('checkoutPage.card')}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.reference')}</span>
                  <strong>-</strong>
                </div>
                <div className="summary-row total">
                  <span>{t('orderConfirmationPage.paidAmount')}</span>
                  <strong className="montant">{formatPriceDzd(computedTotal)}</strong>
                </div>
              </section>

              <section className="confirmation-section">
                <h2><TruckIcon className="section-icon" style={{ width: 24, height: 24, display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('orderConfirmationPage.deliveryTitle')}</h2>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.recipient')}</span>
                  <strong>{order.customer_name}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.address')}</span>
                  <strong>{order.customer_address}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.city')}</span>
                  <strong>{order.customer_postal_code} {order.customer_city}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.phone')}</span>
                  <strong>{order.customer_phone}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('orderConfirmationPage.instructions')}</span>
                  <strong>{deliveryNotes}</strong>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t('orderConfirmationPage.carrier')}</span>
                  <strong className="summary-value">{t('orderConfirmationPage.noneCarrier')}</strong>
                </div>
              </section>

              <section className="confirmation-section products-section">
                <h2><CubeIcon className="section-icon" style={{ width: 24, height: 24, display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('orderConfirmationPage.productsTitle')}</h2>
                <div className="products-list">
                  {parsedItems.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} className="product-row">
                      <img
                        src="/images/bottle.png"
                        alt={item.product_name}
                        className="product-thumb"
                      />
                      <div className="product-info">
                        <div className="product-name">{item.product_name}</div>
                        <div className="confirmation-muted">{t('orderConfirmationPage.unitPrice')} {formatPrice(item.price || 0)}</div>
                      </div>
                      <div className="product-qty">{t('orderConfirmationPage.qty')} {item.quantity}</div>
                      <div className="product-total">{formatPrice(item.total || 0)}</div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {!user && (
              <div className="account-creation-section">
                <p className="account-creation-text">
                  {t('orderConfirmationPage.createAccountPrompt')}
                </p>
                {!showRegisterForm ? (
                  <button
                    onClick={() => setShowRegisterForm(true)}
                    className="account-creation-button"
                  >
                    <UserPlusIcon style={{ width: '20px', height: '20px' }} />
                    {t('orderConfirmationPage.createAccount')}
                  </button>
                ) : (
                  <form onSubmit={handleRegister} className="register-form">
                    {registerErrors.general && (
                      <div className="register-form-error-general">
                        {registerErrors.general}
                      </div>
                    )}

                    <div className="register-form-group">
                      <input
                        type="text"
                        name="fullName"
                        value={registerForm.fullName}
                        onChange={handleRegisterChange}
                        placeholder={t('orderConfirmationPage.registerFullNamePlaceholder')}
                        className={`register-form-input ${registerErrors.fullName ? 'error' : ''}`}
                      />
                      {registerErrors.fullName && (
                        <span className="register-form-error">{registerErrors.fullName}</span>
                      )}
                    </div>

                    <div className="register-form-group">
                      <input
                        type="email"
                        name="email"
                        value={registerForm.email}
                        onChange={handleRegisterChange}
                        placeholder={t('orderConfirmationPage.registerEmailPlaceholder')}
                        className={`register-form-input ${registerErrors.email ? 'error' : ''}`}
                      />
                      {registerErrors.email && (
                        <span className="register-form-error">{registerErrors.email}</span>
                      )}
                    </div>

                    <div className="register-form-group">
                      <input
                        type="password"
                        name="password"
                        value={registerForm.password}
                        onChange={handleRegisterChange}
                        placeholder={t('orderConfirmationPage.registerPasswordPlaceholder')}
                        className={`register-form-input ${registerErrors.password ? 'error' : ''}`}
                      />
                      {registerErrors.password && (
                        <span className="register-form-error">{registerErrors.password}</span>
                      )}
                    </div>

                    <div className="register-form-group">
                      <input
                        type="password"
                        name="confirmPassword"
                        value={registerForm.confirmPassword}
                        onChange={handleRegisterChange}
                        placeholder={t('orderConfirmationPage.registerConfirmPasswordPlaceholder')}
                        className={`register-form-input ${registerErrors.confirmPassword ? 'error' : ''}`}
                      />
                      {registerErrors.confirmPassword && (
                        <span className="register-form-error">{registerErrors.confirmPassword}</span>
                      )}
                    </div>

                    <div className="register-form-actions">
                      <button
                        type="button"
                        onClick={() => setShowRegisterForm(false)}
                        className="register-form-button register-form-button-cancel"
                        disabled={registering}
                      >
                        {t('orderConfirmationPage.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={registering}
                        className="register-form-button register-form-button-submit"
                      >
                        {registering ? 'Création...' : 'Créer le compte'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            <div className="confirmation-actions">
              <Link to="/suivi-commande" className="confirmation-action-link primary">
                Suivre ma commande
              </Link>
              <button
                type="button"
                className="confirmation-action-link secondary"
                onClick={handlePrint}
              >
                Imprimer la confirmation
              </button>
              <Link to="/contact" className="confirmation-action-link secondary">
                Contacter le support
              </Link>
              {user && invoicePdfUrl ? (
                <a className="confirmation-action-link secondary" href={invoicePdfUrl} target="_blank" rel="noreferrer">
                  Télécharger la facture PDF
                </a>
              ) : (
                <span className="confirmation-action-link secondary" aria-disabled="true">
                  Télécharger la facture PDF
                </span>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
